import React, { useState, useEffect } from "react";
import { api, getEntityTypesList } from "../services/api";
import { entitySubtypesCache } from "../services/localCache";
import { createDevConsole } from "../utils/devLogger";

const console = createDevConsole();

const EntityConfiguration = ({ user, hideHeader = false, mapLanguage = false }) => {
  // Get user from prop or localStorage as fallback
  const getCurrentUser = () => {
    if (user) return user;
    try {
      const userStr = localStorage.getItem("user");
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  };
  
  const [currentUser, setCurrentUser] = useState(getCurrentUser());
  const [subtypes, setSubtypes] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [entityTypesLoading, setEntityTypesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEntityType, setSelectedEntityType] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSubtype, setEditingSubtype] = useState(null);
  const [formData, setFormData] = useState({
    entity_type: "",
    subtype_name: "",
    description: "",
    sort_order: 0,
  });

  // Update currentUser when user prop changes
  useEffect(() => {
    if (user) {
      setCurrentUser(user);
    } else {
      try {
        const userStr = localStorage.getItem("user");
        setCurrentUser(userStr ? JSON.parse(userStr) : null);
      } catch {
        setCurrentUser(null);
      }
    }
  }, [user]);

  // Fetch entity types on component mount
  useEffect(() => {
    fetchEntityTypes();
  }, []);

  useEffect(() => {
    if (selectedEntityType) {
      fetchSubtypes();
    }
  }, [selectedEntityType]);

  // Keep form's entity_type in sync with selected tab/type
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      entity_type: selectedEntityType,
    }));
  }, [selectedEntityType]);

  const fetchEntityTypes = async () => {
    setEntityTypesLoading(true);
    try {
      const types = await getEntityTypesList();

      // Filter out duplicates and ensure proper structure
      const uniqueTypes = types.filter(
        (type, index, self) =>
          index === self.findIndex((t) => t.value === type.value)
      );

      setEntityTypes(uniqueTypes);

      // Set the first entity type as default if none selected
      if (!selectedEntityType && uniqueTypes.length > 0) {
        setSelectedEntityType(uniqueTypes[0].value);
      }
      setError(null);
    } catch (err) {
      setError(
        mapLanguage
          ? `Failed to load item types: ${err.message}`
          : `Failed to fetch entity types: ${err.message}`
      );
    } finally {
      setEntityTypesLoading(false);
    }
  };

  const fetchSubtypes = async () => {
    if (!selectedEntityType) return;

    console.log("🔄 Fetching subtypes for entity type:", selectedEntityType);
    setLoading(true);
    try {
      // Clear local cache first to force fresh fetch
      entitySubtypesCache.invalidate();

      // Add cache-busting parameter and bypass_cache to force fresh data from database
      const cacheBuster = `_t=${Date.now()}&bypass_cache=true`;
      const response = await api.getEntitySubtypes(
        selectedEntityType,
        cacheBuster
      );
      console.log("📥 Full API response:", response);
      console.log("📥 Response subtypes:", response.subtypes);
      console.log("📥 Response type:", typeof response);
      console.log("📥 Is array?", Array.isArray(response));

      // Handle different response structures
      let subtypesArray = [];
      if (Array.isArray(response)) {
        // Response is directly an array
        subtypesArray = response;
      } else if (
        response &&
        response.subtypes &&
        Array.isArray(response.subtypes)
      ) {
        // Response has subtypes property
        subtypesArray = response.subtypes;
      } else if (response && Array.isArray(response.data)) {
        // Response has data property
        subtypesArray = response.data;
      }

      console.log("📥 Processed subtypes array:", subtypesArray);
      console.log("📥 Subtypes count:", subtypesArray.length);

      // Filter out only truly invalid entries (null/undefined), but keep subtypes even if they don't have IDs
      const validSubtypes = subtypesArray.filter(
        (subtype) => subtype !== null && subtype !== undefined
      );

      console.log("📥 Valid subtypes after filtering:", validSubtypes);
      console.log("📥 Valid subtypes count:", validSubtypes.length);

      // Log first subtype structure to debug field names
      if (validSubtypes.length > 0) {
        console.log("📥 First subtype structure:", validSubtypes[0]);
        console.log("📥 First subtype keys:", Object.keys(validSubtypes[0]));
        console.log(
          "📥 First subtype subtype_name:",
          validSubtypes[0].subtype_name
        );
        console.log("📥 First subtype name:", validSubtypes[0].name);
      }

      setSubtypes(validSubtypes);
      setError(null);
    } catch (err) {
      console.error("❌ Fetch subtypes error:", err);
      setError(`Failed to fetch subtypes: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubtype = async (e) => {
    e.preventDefault();
    
    // Check if user is in trial mode
    if (currentUser?.is_trial_user && !currentUser?.is_super_user) {
      setError("This feature is restricted in trial mode");
      return;
    }
    
    setLoading(true);
    try {
      // Client-side duplicate pre-check (case-insensitive) to avoid 409s and show existing item
      const nameIn = (formData.subtype_name || "").trim().toLowerCase();
      const dup = subtypes.some(
        (s) => (s.subtype_name || "").trim().toLowerCase() === nameIn
      );
      if (dup) {
        setError(`This subtype already exists for ${selectedEntityType}.`);
        await fetchSubtypes();
        setLoading(false);
        return;
      }

      // Ensure we post with the currently selected entity type
      const payload = { ...formData, entity_type: selectedEntityType };
      const resp = await api.createEntitySubtype(payload);
      // Optimistically add to current list so it shows immediately
      const created = resp?.subtype || resp || {};
      if (
        (created.entity_type || formData.entity_type) === selectedEntityType
      ) {
        setSubtypes((prev) => [
          {
            id: created.id,
            entity_type: created.entity_type || formData.entity_type,
            subtype_name: created.subtype_name || formData.subtype_name,
            description: created.description || formData.description || "",
            is_active: created.is_active ?? true,
            sort_order: created.sort_order ?? (formData.sort_order || 0),
            created_at: created.created_at || null,
            updated_at: created.updated_at || null,
          },
          ...prev,
        ]);
      }
      setShowCreateForm(false);
      setFormData({
        entity_type: selectedEntityType,
        subtype_name: "",
        description: "",
        sort_order: 0,
      });
      // Also fetch to ensure state matches server (in case of server-side defaults)
      await fetchSubtypes();
      setError(null);
    } catch (err) {
      setError(`Failed to create subtype: ${err.message}`);
      // If it already exists, refresh the list so the user can see it
      try {
        await fetchSubtypes();
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubtype = async (e) => {
    e.preventDefault();
    
    // Check if user is in trial mode
    if (currentUser?.is_trial_user && !currentUser?.is_super_user) {
      setError("This feature is restricted in trial mode");
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.updateEntitySubtype(editingSubtype.id, {
        subtype_name: formData.subtype_name,
        description: formData.description,
        sort_order: formData.sort_order,
      });

      // Clear editing state first
      setEditingSubtype(null);
      setFormData({
        entity_type: selectedEntityType,
        subtype_name: "",
        description: "",
        sort_order: 0,
      });

      // Force refresh from server - this is the key fix
      await fetchSubtypes();
      setError(null);
    } catch (err) {
      console.error("Update error:", err);
      setError(`Failed to update subtype: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubtype = async (subtypeId) => {
    // Check if user is in trial mode
    if (currentUser?.is_trial_user && !currentUser?.is_super_user) {
      setError("This feature is restricted in trial mode");
      return;
    }
    
    if (!window.confirm("Are you sure you want to delete this subtype?")) {
      return;
    }
    setLoading(true);
    try {
      await api.deleteEntitySubtype(subtypeId);
      await fetchSubtypes();
      setError(null);
    } catch (err) {
      setError(`Failed to delete subtype: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubtype = (subtype) => {
    setEditingSubtype(subtype);
    setFormData({
      entity_type: subtype.entity_type,
      subtype_name: subtype.subtype_name,
      description: subtype.description || "",
      sort_order: subtype.sort_order,
    });
  };

  const handleCancelEdit = () => {
    setEditingSubtype(null);
    setShowCreateForm(false);
    setFormData({
      entity_type: selectedEntityType,
      subtype_name: "",
      description: "",
      sort_order: 0,
    });
  };

  const toggleSubtypeStatus = async (subtype) => {
    // Check if user is in trial mode
    if (currentUser?.is_trial_user && !currentUser?.is_super_user) {
      setError("This feature is restricted in trial mode");
      return;
    }
    
    setLoading(true);
    try {
      await api.updateEntitySubtype(subtype.id, {
        is_active: !subtype.is_active,
      });
      await fetchSubtypes();
      setError(null);
    } catch (err) {
      setError(`Failed to update subtype status: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const selectedTypeLabel =
    entityTypes.find((t) => t.value === selectedEntityType)?.label ||
    selectedEntityType;

  return (
    <div
      className={
        hideHeader
          ? "bg-gray-900 text-white p-3 sm:p-4"
          : "min-h-screen bg-gray-900 text-white p-4 sm:p-6"
      }
    >
      <div className="mx-auto max-w-6xl">
        {!hideHeader ? (
          <div className="mb-6 sm:mb-8">
            <h1 className="mb-2 text-2xl font-bold text-white sm:text-3xl">
              {mapLanguage ? "Subtypes" : "Entity Subtypes"}
            </h1>
            <p className="text-sm text-gray-400 sm:text-base">
              {mapLanguage
                ? "Optional groupings within an item type (e.g. SaaS vs on-prem for applications)."
                : "Manage entity subtypes for different entity types"}
            </p>
          </div>
        ) : null}

        {error && (
          <div className="mb-4 rounded-lg border border-red-500 bg-red-900/20 p-3 sm:mb-6 sm:p-4">
            <p className="text-sm text-red-400 sm:text-base">{error}</p>
          </div>
        )}

        {/* Entity Type Selector */}
        <div className="mb-4 sm:mb-6">
          <label className="mb-2 block text-sm font-medium text-gray-300">
            {mapLanguage ? "Item type" : "Select Entity Type"}
          </label>
          <select
            value={selectedEntityType}
            onChange={(e) => setSelectedEntityType(e.target.value)}
            disabled={entityTypesLoading}
            className="w-full max-w-md rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {entityTypesLoading ? (
              <option value="">
                {mapLanguage ? "Loading item types..." : "Loading entity types..."}
              </option>
            ) : (
              entityTypes.map((type, index) => (
                <option key={type.value || index} value={type.value}>
                  {type.label}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Create/Edit Form */}
        {(showCreateForm || editingSubtype) && (
          <div className="mb-4 rounded-lg border border-gray-700 bg-gray-800 p-4 sm:mb-6 sm:p-6">
            <h3 className="mb-4 text-base font-semibold text-white sm:text-lg">
              {editingSubtype ? "Edit Subtype" : "Create New Subtype"}
            </h3>
            <form
              onSubmit={
                editingSubtype ? handleUpdateSubtype : handleCreateSubtype
              }
            >
              <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-300">
                    Subtype Name *
                  </label>
                  <input
                    type="text"
                    value={formData.subtype_name}
                    onChange={(e) =>
                      setFormData({ ...formData, subtype_name: e.target.value })
                    }
                    className="w-full rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  {!editingSubtype &&
                    formData.subtype_name.trim() &&
                    subtypes.some(
                      (s) =>
                        (s.subtype_name || "").toLowerCase() ===
                        formData.subtype_name.trim().toLowerCase()
                    ) && (
                      <p className="mt-1 text-sm text-yellow-400">
                        This subtype already exists for {selectedEntityType}.
                      </p>
                    )}
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-300">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sort_order: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-gray-300">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  className="w-full rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="min-h-10 flex-1 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
                >
                  {loading ? "Saving..." : editingSubtype ? "Update" : "Create"}
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="min-h-10 flex-1 rounded-lg bg-gray-600 px-4 py-2 text-white hover:bg-gray-700 sm:flex-none"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Subtypes List */}
        <div className="overflow-hidden rounded-lg border border-gray-700 bg-gray-800">
          <div className="border-b border-gray-700 p-4 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="min-w-0 text-base font-semibold text-white sm:text-xl">
                <span className="break-words">
                  {selectedTypeLabel} Subtypes
                </span>{" "}
                <span className="text-gray-400">({subtypes.length})</span>
              </h2>
              {!showCreateForm && !editingSubtype && (
                currentUser?.is_trial_user && !currentUser?.is_super_user ? (
                  <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
                    <span className="text-sm text-yellow-400">
                      Restricted in trial mode
                    </span>
                    <button
                      disabled
                      className="min-h-10 w-full cursor-not-allowed rounded-lg bg-gray-600 px-4 py-2 text-gray-400 opacity-50 sm:w-auto"
                    >
                      Add Subtype
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowCreateForm(true)}
                    disabled={entityTypesLoading}
                    className={`min-h-10 w-full shrink-0 rounded-lg px-4 py-2 transition-colors sm:w-auto ${
                      entityTypesLoading
                        ? "cursor-not-allowed bg-gray-500 text-gray-300"
                        : "bg-green-600 text-white hover:bg-green-700"
                    }`}
                  >
                    <span className="sm:hidden">
                      {entityTypesLoading ? "Loading..." : "Add"}
                    </span>
                    <span className="hidden sm:inline">
                      {entityTypesLoading ? "Loading..." : "Add Subtype"}
                    </span>
                  </button>
                )
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex h-48 items-center justify-center bg-gray-800 sm:h-64">
              <div className="text-center">
                <svg
                  className="mx-auto mb-4 h-10 w-10 animate-spin text-blue-500 sm:h-12 sm:w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <p className="text-gray-400">Loading subtypes...</p>
              </div>
            </div>
          ) : subtypes.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-400 sm:p-6 sm:text-base">
              No subtypes found for {selectedTypeLabel}. Create one to get
              started.
            </div>
          ) : (
            <div className="divide-y divide-gray-700">
              {subtypes.map((subtype, index) => (
                <div
                  key={subtype.id || `subtype-${index}`}
                  className="p-4 sm:p-6"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <h3 className="break-words text-base font-medium text-white sm:text-lg">
                          {(subtype.subtype_name &&
                            subtype.subtype_name.trim()) ||
                            (subtype.name && subtype.name.trim()) ||
                            `Subtype ID: ${subtype.id || index + 1}`}
                        </h3>
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs ${
                            subtype.is_active
                              ? "border border-green-500 bg-green-900/20 text-green-400"
                              : "border border-red-500 bg-red-900/20 text-red-400"
                          }`}
                        >
                          {subtype.is_active ? "Active" : "Inactive"}
                        </span>
                        <span className="rounded bg-gray-700 px-2 py-0.5 text-xs text-gray-300">
                          Order: {subtype.sort_order}
                        </span>
                      </div>
                      {subtype.description ? (
                        <p className="mb-2 break-words text-sm text-gray-400">
                          {subtype.description}
                        </p>
                      ) : (
                        <p className="mb-2 text-sm italic text-gray-500">
                          No description provided
                        </p>
                      )}
                      <div className="text-xs text-gray-500">
                        {subtype.created_at && (
                          <>
                            Created:{" "}
                            {new Date(subtype.created_at).toLocaleDateString()}
                            {subtype.updated_at &&
                              subtype.updated_at !== subtype.created_at && (
                                <span>
                                  {" "}
                                  · Updated:{" "}
                                  {new Date(
                                    subtype.updated_at
                                  ).toLocaleDateString()}
                                </span>
                              )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex w-full flex-wrap gap-2 sm:ml-0 sm:w-auto sm:shrink-0 sm:justify-end">
                      {currentUser?.is_trial_user && !currentUser?.is_super_user ? (
                        <span className="px-1 py-1 text-sm text-yellow-400">
                          Restricted in trial mode
                        </span>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditSubtype(subtype)}
                            className="min-h-9 flex-1 rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 sm:flex-none"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => toggleSubtypeStatus(subtype)}
                            className={`min-h-9 flex-1 rounded px-3 py-1.5 text-sm sm:flex-none ${
                              subtype.is_active
                                ? "bg-yellow-600 text-white hover:bg-yellow-700"
                                : "bg-green-600 text-white hover:bg-green-700"
                            }`}
                          >
                            {subtype.is_active ? "Deactivate" : "Activate"}
                          </button>
                          <button
                            onClick={() => handleDeleteSubtype(subtype.id)}
                            className="min-h-9 flex-1 rounded bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700 sm:flex-none"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntityConfiguration;
